import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography
} from '@mui/material';
import React, { useState, useEffect } from 'react';

type TFeedbackFormValues = {
    expectedResult: string;
    actualResult: string;
};

type TFeedbackModalProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: TFeedbackFormValues) => void;
    loading?: boolean;
    error?: boolean;
    initialExpectedResult?: string;
    initialActualResult?: string;
};

const FeedbackModal: React.FC<TFeedbackModalProps> = ({
    open,
    onClose,
    onSubmit,
    loading = false,
    error = false,
    initialExpectedResult = '',
    initialActualResult = '',
}) => {
    const [expectedResult, setExpectedResult] =
        useState(initialExpectedResult);
    const [actualResult, setActualResult] =
        useState(initialActualResult);

    useEffect(() => {
        if (open) {
            setExpectedResult(initialExpectedResult || '');
            setActualResult(initialActualResult || '');
        }
    }, [open, initialExpectedResult, initialActualResult]);

    const handleSubmit = () => {
        if (expectedResult.trim() && actualResult.trim()) {
            onSubmit({
                expectedResult,
                actualResult,
            });
            setExpectedResult('');
            setActualResult('');
        }
    };

    const handleClose = () => {
        if (loading) {
            return;
        }

        setExpectedResult('');
        setActualResult('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    width: '600px',
                    maxWidth: '90vw',
                    borderRadius: '12px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: 3,
                    pt: 2.5,
                    pb: 2,
                    borderBottom: '1px solid #E4E7EC',
                    backgroundColor: '#FFFFFF',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            m: 0,
                            fontSize: '18px',
                            fontWeight: 600,
                            lineHeight: '26px',
                            color: '#101828',
                        }}
                    >
                        Report the answer
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            m: 0,
                            color: '#667085',
                            fontSize: '14px',
                            lineHeight: '20px',
                        }}
                    >
                        Not quite what you expected? Flag it for the team.
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent
                sx={{
                    px: 3,
                    pt: 4.5,
                    pb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflow: 'visible',
                }}
            >
                {error && (
                    <Alert severity="error">
                        Failed to submit report. Please try again.
                    </Alert>
                )}
                <Box sx={{ mt: 1 }}>
                    <Box
                        component="label"
                        htmlFor="expected_result"
                        sx={{
                            display: 'block',
                            mb: 1,
                            color: '#344054',
                            fontFamily: 'DM Sans',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                        }}
                    >
                        A better response
                    </Box>
                    <TextField
                        autoFocus
                        id="expected_result"
                        placeholder="What would've been a better answer?"
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={expectedResult}
                        sx={{
                            m: 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            },
                            '& .MuiInputBase-input': {
                                fontFamily: 'DM Sans',
                                fontSize: '14px',
                                color: '#101828',
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: '#98A2B3',
                                opacity: 1,
                            },
                        }}
                        onChange={(e) => {
                            setExpectedResult(e.target.value);
                        }}
                        disabled={loading}
                    />
                </Box>
                <Box>
                    <Box
                        component="label"
                        htmlFor="actual_result"
                        sx={{
                            display: 'block',
                            mb: 1,
                            color: '#344054',
                            fontFamily: 'DM Sans',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                        }}
                    >
                        Paul's response
                    </Box>
                    <TextField
                        id="actual_result"
                        placeholder="What did Paul actually say instead?"
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={actualResult}
                        sx={{
                            m: 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            },
                            '& .MuiInputBase-input': {
                                fontFamily: 'DM Sans',
                                fontSize: '14px',
                                color: '#101828',
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: '#98A2B3',
                                opacity: 1,
                            },
                        }}
                        onChange={(e) => {
                            setActualResult(e.target.value);
                        }}
                        disabled={loading}
                    />
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #E4E7EC',
                    backgroundColor: '#F9FAFB',
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                    color="inherit"
                    sx={{
                        minWidth: 112,
                        px: 2.5,
                        py: 1.5,
                        borderRadius: '8px',
                        border: '1px solid #D0D5DD',
                        color: '#475467',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        backgroundColor: '#FFFFFF !important',
                        boxShadow: 'none',
                        '&:hover': {
                            border: '1px solid #D0D5DD',
                            backgroundColor: '#F9FAFB !important',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#FFFFFF !important',
                            border: '1px solid #D0D5DD',
                            color: '#98A2B3',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={
                        loading ||
                        !expectedResult.trim() ||
                        !actualResult.trim()
                    }
                    variant="contained"
                    color="inherit"
                    sx={{
                        minWidth: 132,
                        px: 2.5,
                        py: 1.5,
                        borderRadius: '8px',
                        backgroundColor: '#00B4D8',
                        color: '#FFFFFF !important',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#0096C7',
                            color: '#FFFFFF !important',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#B9EAF3',
                            color: '#FFFFFF !important',
                        },
                    }}
                    startIcon={
                        loading ? <CircularProgress size={16} color="inherit" /> : null
                    }
                >
                    {loading ? 'Updating...' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackModal;
